import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Container, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { IEvent } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme as osTheme, theme } from '../../constants/theme';
import FilterIcon from '../CustomSVGIcons/FilterIcon';
import { EventCard } from './EventCard';
import { EventWrapper } from './EventWrapper';
import { ReusableDrawer } from '../ReusableDrawer/ReusableDrawer';
import { IEventFilter } from '../../redux/events/types';
import { isString } from 'lodash';
import { useDispatch } from 'react-redux';
import { setRecommendation } from '../../redux/appConfigurator/action';

const niceColors = require('nice-color-palettes');

export interface IEventGrid {
    events: IEvent[];
    selectedFilters?: IEventFilter;
    deleteFilter?: (filter: IEventFilter) => void;
    totalEvents?: number;
}

export const EventGrid: React.FC<IEventGrid> = ({ events, selectedFilters, deleteFilter, totalEvents = 0 }) => {
    const [filter, setFilter] = useState(['total']);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
    const [open, setOpen] = useState(false);
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();

    const handleOnRecommendClick = (type: string, model: any) => {
        dispatch(
            setRecommendation({
                type: type as any, // Type this properly
                document: model,
            }),
        );
    };

    const colorsNice = [...niceColors[67], ...niceColors[1], ...niceColors[29], ...niceColors[71], ...niceColors[81]];

    const handleClose = () => {
        setOpen(false);
        setSelectedEvent(null);
    };

    const handleSetEvent = (event: IEvent) => {
        setSelectedEvent(event);
        setOpen(true);
    };

    const handleDelete = (label: string) => {
        if (!selectedFilters) {
            return;
        }
        if (label.toLowerCase() === 'total') {
            return selectedFilters;
        }

        const updatedFilters = Object.keys(selectedFilters || {}).reduce((acc, key) => {
            const filter = selectedFilters[key as keyof IEventFilter];
            const { displayValue, value } = filter;

            if (Array.isArray(displayValue) && Array.isArray(value)) {
                const filteredDisplayValue = displayValue.filter(item => item.toLowerCase() !== label.toLowerCase());
                const filteredValue = value.filter(item => item.toLowerCase() !== label.toLowerCase());

                if (filteredDisplayValue.length > 0) {
                    acc[key as keyof IEventFilter] = {
                        ...filter,
                        displayValue: filteredDisplayValue,
                        value: filteredValue,
                    };
                }
            } else if (
                isString(displayValue) &&
                isString(value) &&
                displayValue.toLowerCase() !== label.toLowerCase() &&
                value.toLowerCase() !== label.toLowerCase()
            ) {
                acc[key as keyof IEventFilter] = {
                    ...filter,
                    displayValue,
                    value,
                };
            }

            return acc;
        }, {} as typeof selectedFilters);
        deleteFilter && deleteFilter(updatedFilters);
        return updatedFilters;
    };

    const extractFilters = useCallback(() => {
        const result: any[] = [];
        selectedFilters &&
            Object.keys(selectedFilters).forEach(key => {
                const value = selectedFilters[key as keyof IEventFilter].displayValue;
                if (Array.isArray(value)) {
                    result.push(...value.filter(item => typeof item === 'string')); // Ensure strings only
                } else {
                    result.push(value);
                }
            });
        return result;
    }, [selectedFilters]);
    useEffect(() => {
        if (selectedFilters) {
            setFilter(prevState => {
                const updatedFilters = ['total', ...extractFilters()];
                return [...new Set(updatedFilters)];
            });
        } else {
            setFilter(['total']);
        }
    }, [selectedFilters, extractFilters]);

    return (
        <Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography
                    variant="h1"
                    component="h2"
                    fontWeight="bold"
                    color={osTheme[themeStyle].tag_white}
                    fontSize={32}>
                    Live Events
                </Typography>

                {isMobile && <FilterIcon height={'30px'} width={'30px'} stroke={osTheme[themeStyle].tag_white} />}
            </Stack>
            <ReusableDrawer
                open={open}
                onClose={handleClose}
                anchor="right"
                content={
                    !!selectedEvent && (
                        <Container sx={{ height: '100vh', overflowY: 'auto', padding: 2 }}>
                            <EventWrapper event={selectedEvent} disableSuggestion />
                        </Container>
                    )
                }
            />
            <Box
                display="flex"
                flexWrap="wrap"
                gap={1} // Adds spacing between items
                mb={1}>
                {filter.map((label, index) => (
                    <Chip
                        key={index}
                        label={label === 'total' ? `${label} (${totalEvents})` : label}
                        onDelete={() => handleDelete(label)}
                        sx={{
                            textTransform: 'capitalize',
                            backgroundColor: colorsNice[index],
                            color: theme[themeStyle].true_black,
                        }}
                    />
                ))}
            </Box>

            <Grid
                container
                spacing={2}
                mr={{
                    lg: 0,
                }}>
                {events.map((event, index) => (
                    <Grid item xs={12} sm={4} md={6} lg={4} key={index}>
                        <EventCard
                            event={event}
                            onClickEvent={() => handleSetEvent(event)}
                            onRecommendationClick={() => {
                                handleOnRecommendClick('event', event);
                            }}
                        />
                    </Grid>
                ))}
            </Grid>
        </Stack>
    );
};
